import React from 'react';
import _ from 'lodash';
import SimpleModalContent, {ICON_TYPES} from './SimpleModalContent';
import {defer, servicesProxy} from '@elixir/platform';
import ModalProvider from './ModalProvider';
import {log} from '@elixir/logger';

/**
 * Helper method that is designed to show a message within a component inside of a `Modal` dialog.  The `messageProps`
 * are passed to the `MessageComponent` react component for rendering inside of the `Modal` dialog that can be
 * configured via the optional set of `modalProps`.
 *
 * @param {Object} messageProps - The props that are to be provided to the `MessageComponent`
 * @param {function} MessageComponent - The React Component function for the Component to render in the message body
 *          of the `Modal`
 * @param {Object} [modalProps={}] - Optional set of props to pass to the `Modal` dialog, will be merged together with
 *          the defaults and the props that are required for the `Modal` to work with the `ModalService`
 * @returns {{result: Promise, opened: Promise, close: (function(*=)), dismiss: (function(*=))}} - The object that
 *      has the rendered dialog's `opened` and `result` promises as well as methods to close or dismiss the modal
 *      programmatically
 * @private
 */
const _showMessage = (messageProps, MessageComponent, modalProps = {}) => {
    const resultDefer = defer();
    const openedDefer = defer();
    const messageComponent = <MessageComponent {...messageProps}/>;
    const defaultModalProps = {
        backdrop: true
    };
    const requiredProps = {
        resultDefer,
        toggle: null, // defined here to invoke a warning, will be overridden by ModalProvider
        isOpen: null, // defined here to invoke a warning, will be overridden by ModalProvider
        children: messageComponent
    };
    Object.keys(requiredProps).forEach(key => {
        if (modalProps[key]) {
            log.warning(`ModalService: "modalProps.${key}" has value "${modalProps[key]}" that is being replaced by a required one`);
        }
    });
    const allModalOptions = Object.assign({}, defaultModalProps, modalProps, requiredProps, {
        onOpened: () => {
            openedDefer.resolve();
            if (_.isFunction(modalProps.onOpened)) {
                modalProps.onOpened();
            }
        }
    });

    ModalProvider.showDialogWithProps(allModalOptions);

    return {
        result: resultDefer.promise,
        opened: openedDefer.promise,
        close(outcome) {
            ModalProvider.closeModal(outcome);
        },
        dismiss(reason) {
            ModalProvider.dismissModal(reason);
        }
    };
};

/**
 * This method checks whether the params object in each of the functions inside 'servicesProxy' below contains
 * the required 'title' and 'message' properties
 * @param {Object} params - contains properties to be validated
 * @returns {boolean} true if messageProps contains title and message properties
 */
const validateProperties = (params) => {
    if (!('title' in params) || !('message' in params)) {
        log.warning('You are attempting to show a dialog with no title or message');
        return false;
    }
    return true;
};

/**
 * The `ModalServices` service provides an API that supports displaying a variety of different styles of Modal dialogs.
 *
 * This was adapted from:
 *   `.../frontend/js/src/edge/platform/widgets/modal/ModalService.js`
 * and
 *   `.../frontend/js/src/edge/platform/widgets/modal/ModalProvider.js`
 */
export default servicesProxy({

    showMessage: (messageProps, MessageComponent, modalProps = {}) => {
        if (__TEST__) {
            return _showMessage(messageProps, MessageComponent, modalProps);
        }
        log.warning('_showMessage should be used only for test purposes');
    },

    /**
     * Show a dialog that is meant to present information to the user with a single action button.
     *
     * @param {Object} params - The properties that will be used by the method
     * @param {string} params.title - The title to use for the dialog
     * @param {string | ReactNode} params.message - The basic message information to show in the dialog
     * @param {string} [params.description] - Any additional description to add to the dialog
     * @param {string} [params.actionLabel] - The optional label for the action button, defaults to `Ok`
     * @param {string} [params.actionBtnClass] - The optional CSS class for the action button, defaults to `Modal-hashed__buttonPrimary__*`
     * @param {string} [params.icon] - Input that will override the default icon for the modal
     * @returns {{result: Promise, opened: Promise, close: (function(*=)), dismiss: (function(*=))}} - The object that
     *      has the rendered dialog's `opened` and `result` promises as well as methods to close or dismiss the modal
     *      programmatically
     */
    showInfo: params => {
        if (!validateProperties(params)) {
            return;
        }
        const messageProps = Object.assign({iconClassType: ICON_TYPES.INFO_ICON}, params);

        return _showMessage(messageProps, SimpleModalContent, {backdrop: 'static'});
    },

    /**
     * Show a dialog that is meant to obtain an email address from the user, with a single action button.
     *
     * @param {Object} params - The properties that will be used by the method
     * @param {string} params.title - The title to use for the dialog
     * @param {string | ReactNode} params.message - The basic message information to show in the dialog
     * @param {string} [params.description] -  Any additional description to add to the dialog
     * @param {string} [params.actionLabel] - The optional label for the action button, defaults to `Ok`
     * @param {string} [params.actionBtnClass] - The optional CSS class for the action button, defaults to `Modal-hashed__buttonPrimary__*`
     * @param {string} [params.icon] -  Input that will override the default icon for the modal
     * @returns {{result: Promise, opened: Promise, close: (function(*=)), dismiss: (function(*=))}} - The object that
     *      has the rendered dialog's `opened` and `result` promises as well as methods to close or dismiss the modal
     *      programmatically
     */
    showEmail: params => {
        if (!validateProperties(params)) {
            return;
        }
        const messageProps = Object.assign({iconClassType: ICON_TYPES.EMAIL_ICON}, params);
        return _showMessage(messageProps, SimpleModalContent, {backdrop: 'static'});
    },

    /**
     * Show a dialog that is meant to confirm (or cancel) an action by asking the user, with two action buttons.
     *
     * @param {Object} params - The properties that will be used by the method
     * @param {string} params.title - The title to use for the dialog
     * @param {string | ReactNode} params.message - The basic message information to show in the dialog
     * @param {string} [params.description] - Any additional description to add to the dialog
     * @param {string} [params.actionLabel] - The optional label for the action button, defaults to `Ok`
     * @param {string} [params.actionBtnClass] -  The optional CSS class for the action button, defaults to `Modal-hashed__buttonPrimary__*`
     * @param {string} [params.cancelLabel] - The optional label for the cancel button, defaults to `Close`
     * @param {boolean} [params.showCancelBtn] - Optional flag that controls showing the cancel button, defaults to `true`
     * @param {Array} [params.radioOptions] - Optional list of options to present as radio buttons
     * @param {string} [params.icon] -  Input that will override the default icon for the modal
     * @returns {{result: Promise, opened: Promise, close: (function(*=)), dismiss: (function(*=))}} - The object that
     *      has the rendered dialog's `opened` and `result` promises as well as methods to close or dismiss the modal
     *      programmatically
     */
    showConfirm: params => {
        if (!validateProperties(params)) {
            return;
        }
        const messageProps =
            Object.assign({
                showCancelBtn: true,
                iconClassType: ICON_TYPES.QUESTION_ICON
            }, params);
        return _showMessage(messageProps, SimpleModalContent);
    },

    /**
     * Show a dialog that is meant to warn the user, with one or two action buttons.
     *
     * @param {Object} params - The properties that will be used by the method
     * @param {string} params.title - The title to use for the dialog
     * @param {string | ReactNode} params.message - The basic message information to show in the dialog
     * @param {string} [params.description] - Any additional description to add to the dialog
     * @param {string} [params.actionLabel] - The optional label for the action button, defaults to `Yes`
     * @param {string} [params.actionBtnClass] -  The optional CSS class for the action button, defaults to `Modal-hashed__buttonPrimary__*`
     * @param {string} [params.cancelBtnLabel] - The optional label for the cancel button, defaults to `Close`
     * @param {Array} [params.radioOptions] - Optional list of options to present as radio buttons
     * @param {ReactNode} [params.additionalContent] - Any additional content that is to be rendered into the message body
     * @param {boolean} [params.showCancelBtn] - Optional flag that controls showing the cancel button, defaults to `true`
     * @param {Object} [params.checkboxOptions] - Optional object that contains information needed to render a checkbox
     * @param {string} [params.icon] -  Input that will override the default icon for the modal
     * @returns {{result: Promise, opened: Promise, close: (function(*=)), dismiss: (function(*=))}} - The object that
     *      has the rendered dialog's `opened` and `result` promises as well as methods to close or dismiss the modal
     *      programmatically
     */
    showWarning: params => {
        if (!validateProperties(params)) {
            return;
        }
        const messageProps = Object.assign({
            actionLabel: {
                id: 'platform.modal.ModalService.Yes',
                defaultMessage: 'Yes'
            },
            showCancelBtn: true,
            iconClassType: ICON_TYPES.WARNING_ICON
        }, params);
        return _showMessage(messageProps, SimpleModalContent);
    },

    /**
     * Show a dialog that is meant to present error information to the user, with a single action button.
     *
     * @param {Object} params - The properties that will be used by the method
     * @param {string} params.title - The title to use for the dialog
     * @param {string | ReactNode} params.message - The basic message information to show in the dialog
     * @param {string} [params.description] - Any additional description to add to the dialog
     * @param {string} [params.actionLabel] - The optional label for the action button, defaults to `Close`
     * @param {string} [params.actionBtnClass] -  The optional CSS class for the action button, defaults to `Modal-hashed__buttonPrimary__*`
     * @returns {{result: Promise, opened: Promise, close: (function(*=)), dismiss: (function(*=))}} - The object that
     *      has the rendered dialog's `opened` and `result` promises as well as methods to close or dismiss the modal
     *      programmatically
     */
    showError: params => {
        if (!validateProperties(params)) {
            return;
        }
        const messageProps = Object.assign({
            actionLabel: {
                id: 'platform.modal.ModalService.Close',
                defaultMessage: 'Close'
            },
            iconClassType: ICON_TYPES.ERROR_ICON
        }, params);
        return _showMessage(messageProps, SimpleModalContent);
    },

    /**
     * Show a dialog that is meant to present success information to the user, with a single action button.
     *
     * @param {Object} params - The properties that will be used by the method
     * @param {string} params.title - The title to use for the dialog
     * @param {string | ReactNode} params.message - The basic message information to show in the dialog
     * @param {string} [params.description] - Any additional description to add to the dialog
     * @param {string} [params.actionLabel] - The optional label for the action button, defaults to `Ok`
     * @param {string} [params.actionBtnClass] -  The optional CSS class for the action button, defaults to `Modal-hashed__buttonPrimary__*`
     * @param {string} [params.icon] -  Input that will override the default icon for the modal
     * @returns {{result: Promise, opened: Promise, close: (function(*=)), dismiss: (function(*=))}} - The object that
     *      has the rendered dialog's `opened` and `result` promises as well as methods to close or dismiss the modal
     *      programmatically
     */
    showSuccess: params => {
        if (!validateProperties(params)) {
            return;
        }
        const messageProps = Object.assign({iconClassType: ICON_TYPES.SUCCESS_ICON}, params);
        return _showMessage(messageProps, SimpleModalContent);
    },

    /**
     * Show a full screen dialog to the user that contains the given `<ModalContent {...contentProps}/>`
     *
     * @param {function} MessageComponent - The React Component function for the Component to render in the message body
     *          of the `Modal`
     * @param {Object} messageProps - The props that are to be provided to the `MessageComponent`
     * @param {Object} modalProps - Optional set of props to pass to the `Modal` dialog, meant to override the defaults
     * @returns {{result: Promise, opened: Promise, close: (function(*=)), dismiss: (function(*=))}}
     *
     * @example
     *
     * class Example extends Component {
     *          closeModal = () => {
     *          console.log('Close modal!')
     *          this.modal.dismiss('Close msg here');
     *      }
     *
     *      confirmModal = async () => {
     *          console.log('Confirmed!')
     *          this.modal.close('Confirm msg here');
     *      }
     *
     *      showModal = () => {
     *          const onOk = this.confirmModal;
     *          const onCancel = this.closeModal;
     *
     *          const popup = ({onOk, onCancel}) => (
     *              <div>
     *                  Full screen popup example!
     *                  <button onClick={onOk}>Ok</button>
     *                  <button onClick={onCancel}>Cancel</button>
     *              </div>
     *          );
     *
     *          this.modal = ModalService.showFullScreenPopup(
     *              popup,
     *              {
     *                  onOk,
     *                  onCancel,
     *                  id: 'popupId',
     *                  isOpen: true
     *              }
     *          );
     *
     *          this.modal.result
     *              .then((msg)=> console.log('then called', msg))
     *              .catch((msg)=> console.log('catch called', msg));
     *      }
     * }
     */
    showFullScreenPopup: (MessageComponent, messageProps, modalProps) => {
        const dialog = _showMessage(messageProps, MessageComponent, Object.assign({}, modalProps, {fullScreen: true}));
        dialog.result.catch(err => err); // default catch of cancel/reject
        return dialog;
    },

    showPopover: (MessageComponent, messageProps, modalProps) => {
        const dialog = _showMessage(messageProps, MessageComponent, Object.assign({}, modalProps, {popOver: true}));
        dialog.result.catch(err => err); // default catch of cancel/reject
        return dialog;
    },

    /**
     * Shows a dialog composed of external React component. The external component should render `<ModalDialog>`
     * so that it can make the appropriate calls to close or dismiss the dialog.
     *
     * @param {function} MessageComponent - The React Component function for the Component to render in the message body
     *          of the `Modal`
     * @param {Object} messageProps - The props that are to be provided to the `MessageComponent`
     * @param {Object} modalProps - Optional set of props to pass to the `Modal` dialog, meant to override the defaults
     * @returns {{result: Promise, opened: Promise, close: (function(*=)), dismiss: (function(*=))}}
     */
    showSimpleDialog: (MessageComponent, messageProps, modalProps) => {
        const dialog = _showMessage(messageProps, MessageComponent, modalProps);
        dialog.result.catch(err => err); // default catch of cancel/reject
        return dialog;
    }
});
